import L from "leaflet";
import {
    type Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { Rectangle, useMap } from "react-leaflet";

import colors from "@/styles/colors";

interface BoxSelectorResult {
    layer: {
        _bounds: L.LatLngBounds;
    };
}

interface BoxSelectorProps {
    /**
     * Callback invoked when a rectangle selection is complete.
     */
    onCreated?: (result: BoxSelectorResult) => void;
    /**
     * The color of the selection rectangle.
     */
    color?: string;
    /**
     * Ref to expose methods for enabling/disabling the box selector.
     */
    ref?: Ref<BoxSelectorRef>;
}

interface BoxSelectorRef {
    /**
     * Enables the box selector, allowing the user to draw a rectangle on the map.
     */
    enable: () => void;
    /**
     * Disables the box selector, preventing the user from drawing a rectangle.
     */
    disable: () => void;
    /**
     * Returns whether the box selector is currently enabled.
     */
    enabled: () => boolean;
}

/**
 * Component for selecting an area on a map by drawing a rectangle.
 * Adapted from MapAreaSelect in atrius-admin-react.
 *
 * @param root0 - The component props. `ref` exposes `enable()`, `disable()`,
 *   and `enabled()`.
 * @returns The selection rectangle, or null while inactive.
 */
const BoxSelector = ({
    onCreated,
    color = colors.skyHover,
    ref,
}: BoxSelectorProps) => {
    const map = useMap();
    const [startPoint, setStartPoint] = useState(null);
    const [endPoint, setEndPoint] = useState(null);
    const [isDragging, setDragging] = useState(false);
    const [isEnabled, setEnabled] = useState(false);

    // Use refs for touch handlers to access current state without re-binding
    const isDraggingRef = useRef(isDragging);
    const startPointRef = useRef(startPoint);
    const endPointRef = useRef(endPoint);

    // Keep refs in sync with state
    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);

    useEffect(() => {
        startPointRef.current = startPoint;
    }, [startPoint]);

    useEffect(() => {
        endPointRef.current = endPoint;
    }, [endPoint]);

    // Expose enable/disable methods via ref to match old L.Draw.Rectangle API
    useImperativeHandle(
        ref,
        () => ({
            enable: () => setEnabled(true),
            disable: () => setEnabled(false),
            enabled: () => isEnabled,
        }),
        [isEnabled],
    );

    const handleMouseDown = useCallback((e) => {
        setDragging(true);
        setStartPoint(e.latlng);
        setEndPoint(null);
    }, []);

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDragging) return;
            setEndPoint(e.latlng);
        },
        [isDragging],
    );

    const handleMouseUp = useCallback(() => {
        if (!isDragging || !startPoint) return;

        setDragging(false);
        setStartPoint(null);

        if (!endPoint) return;

        setEndPoint(null);

        // Create bounds from the selected rectangle
        const bounds = L.latLngBounds(
            [startPoint.lat, startPoint.lng],
            [endPoint.lat, endPoint.lng],
        );

        const result = {
            layer: {
                _bounds: bounds,
            },
        };

        onCreated?.(result);
    }, [endPoint, isDragging, onCreated, startPoint]);

    const handleRightClick = useCallback(() => {
        if (!isDragging) return;
        setDragging(false);
        setStartPoint(null);
        setEndPoint(null);
    }, [isDragging]);

    const handleTouchStart = useCallback(
        (e) => {
            if (e.touches.length !== 1) return;

            e.preventDefault();
            const touch = e.touches[0];
            const latlng = map.containerPointToLatLng(
                L.point(touch.clientX, touch.clientY),
            );

            setDragging(true);
            setStartPoint(latlng);
            setEndPoint(null);
        },
        [map],
    );

    const handleTouchMove = useCallback(
        (e) => {
            if (!isDraggingRef.current || e.touches.length !== 1) return;

            e.preventDefault();
            const touch = e.touches[0];
            const latlng = map.containerPointToLatLng(
                L.point(touch.clientX, touch.clientY),
            );

            setEndPoint(latlng);
        },
        [map],
    );

    const handleTouchEnd = useCallback(
        (e) => {
            if (!isDraggingRef.current || !startPointRef.current) return;

            e.preventDefault();

            const currentStartPoint = startPointRef.current;
            const currentEndPoint = endPointRef.current;

            setDragging(false);
            setStartPoint(null);

            if (!currentEndPoint) return;

            setEndPoint(null);

            // Create bounds from the selected rectangle
            const bounds = L.latLngBounds(
                [currentStartPoint.lat, currentStartPoint.lng],
                [currentEndPoint.lat, currentEndPoint.lng],
            );

            const result = {
                layer: {
                    _bounds: bounds,
                },
            };

            onCreated?.(result);
        },
        [onCreated],
    );

    useEffect(() => {
        if (!map || !isEnabled) {
            return undefined;
        }

        const container = map.getContainer();

        map.dragging.disable();

        // Mouse events (via Leaflet)
        map.on("mousedown", handleMouseDown);
        map.on("mousemove", handleMouseMove);
        map.on("mouseup", handleMouseUp);
        map.on("contextmenu", handleRightClick);

        // Touch events (native, with passive: false to allow preventDefault)
        container.addEventListener("touchstart", handleTouchStart, {
            passive: false,
        });
        container.addEventListener("touchmove", handleTouchMove, {
            passive: false,
        });
        container.addEventListener("touchend", handleTouchEnd, {
            passive: false,
        });

        return () => {
            map.dragging.enable();
            map.off("mousedown", handleMouseDown);
            map.off("mousemove", handleMouseMove);
            map.off("mouseup", handleMouseUp);
            map.off("contextmenu", handleRightClick);

            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleRightClick,
        handleTouchEnd,
        handleTouchMove,
        handleTouchStart,
        isEnabled,
        map,
    ]);

    if (!isEnabled || !startPoint || !endPoint) return null;

    return (
        <Rectangle
            bounds={[
                [startPoint.lat, startPoint.lng],
                [endPoint.lat, endPoint.lng],
            ]}
            color={color}
        />
    );
};

export default BoxSelector;
